/**
 * ADMIN MODULE — Email Nurture data access.
 * Whitelisted reads/writes over the nurture tables (RLS pins them to platform
 * admins). Components call these instead of touching supabase directly, mirroring
 * flywheelService / seoService. Errors are logged here and surfaced generic.
 *
 * The send engine (edge functions, service role) owns delivery; this layer is
 * for the admin UI: monitoring + manual controls.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";

function fail(where, error, msg) {
  console.error(`[nurtureService.${where}]`, error);
  throw new Error(msg);
}

async function count(table, build) {
  let q = supabase.from(table).select("*", { count: "exact", head: true });
  if (build) q = build(q);
  const { count: c, error } = await q;
  if (error) { console.error("[nurtureService.count]", table, error); return null; }
  return c ?? 0;
}

// ---- Subscriber list ----
export async function listSubscribers({ status = "all", limit = 200 } = {}) {
  let q = supabase
    .from("nurture_subscribers")
    .select("id, email, first_name, source, sequence_key, current_step, next_send_at, status, created_at, updated_at")
    .order("updated_at", { ascending: false })
    .limit(limit);
  if (status && status !== "all") q = q.eq("status", status);
  const { data, error } = await q;
  if (error) fail("listSubscribers", error, "Could not load subscribers.");
  return data ?? [];
}

// ---- Funnel metrics ----
export async function getFunnelMetrics() {
  const [enrolled, active, paused, completed, unsubscribed, bounced, converted, sends, opens, clicks] =
    await Promise.all([
      count("nurture_subscribers"),
      count("nurture_subscribers", (q) => q.eq("status", "active")),
      count("nurture_subscribers", (q) => q.eq("status", "paused")),
      count("nurture_subscribers", (q) => q.eq("status", "completed")),
      count("nurture_subscribers", (q) => q.eq("status", "unsubscribed")),
      count("nurture_subscribers", (q) => q.eq("status", "bounced")),
      count("nurture_subscribers", (q) => q.eq("status", "converted")),
      count("nurture_sends"),
      count("nurture_sends", (q) => q.not("opened_at", "is", null)),
      count("nurture_sends", (q) => q.not("clicked_at", "is", null)),
    ]);
  const rate = (a, b) => (b ? Math.round((a / b) * 100) : 0);
  return {
    enrolled, active, paused, completed, unsubscribed, bounced, converted,
    sends, opens, clicks,
    openRate: rate(opens, sends),
    clickRate: rate(clicks, sends),
  };
}

// ---- Manual controls ----
export async function pauseSubscriber(id) {
  const { error } = await supabase.from("nurture_subscribers").update({ status: "paused" }).eq("id", id);
  if (error) fail("pauseSubscriber", error, "Could not pause.");
}

export async function resumeSubscriber(id) {
  const { error } = await supabase
    .from("nurture_subscribers")
    .update({ status: "active", next_send_at: new Date().toISOString() })
    .eq("id", id);
  if (error) fail("resumeSubscriber", error, "Could not resume.");
}

export async function markConverted(id) {
  const { error } = await supabase.from("nurture_subscribers").update({ status: "converted" }).eq("id", id);
  if (error) fail("markConverted", error, "Could not mark converted.");
}

// Replay from a given step: clears that step's (and later) send log, points the
// subscriber back at it, due now, active. The engine re-sends on the next tick.
export async function resendStep(id, step) {
  const s = Number(step) || 0;
  const { error: delErr } = await supabase
    .from("nurture_sends").delete().eq("subscriber_id", id).gte("step", s);
  if (delErr) fail("resendStep.delete", delErr, "Could not reset sends.");
  const { error } = await supabase
    .from("nurture_subscribers")
    .update({ current_step: s, next_send_at: new Date().toISOString(), status: "active" })
    .eq("id", id);
  if (error) fail("resendStep.update", error, "Could not queue resend.");
}

// Per-subscriber send history (for an expanded row, if the UI wants it).
export async function listSends(subscriberId) {
  const { data, error } = await supabase
    .from("nurture_sends")
    .select("step, provider_msg_id, sent_at, opened_at, clicked_at")
    .eq("subscriber_id", subscriberId)
    .order("step", { ascending: true });
  if (error) fail("listSends", error, "Could not load send history.");
  return data ?? [];
}

// ---- Template editor (copy lives in the DB; engine renders from it) ----
export const NURTURE_SEQUENCE_KEY = "build-the-brand";

export async function listTemplates(sequenceKey = NURTURE_SEQUENCE_KEY) {
  const { data, error } = await supabase
    .from("nurture_templates")
    .select("id, sequence_key, step, subject, preview_text, html, text, is_active, template_id, interval_days, updated_at, updated_by")
    .eq("sequence_key", sequenceKey)
    .order("step", { ascending: true });
  if (error) fail("listTemplates", error, "Could not load templates.");
  return data ?? [];
}

export async function saveTemplate(id, fields) {
  const { data: { user } } = await supabase.auth.getUser();
  const patch = {
    subject: String(fields.subject ?? "").slice(0, 300),
    preview_text: fields.preview_text != null ? String(fields.preview_text).slice(0, 300) : null,
    html: String(fields.html ?? ""),
    text: String(fields.text ?? ""),
    is_active: !!fields.is_active,
    template_id: fields.template_id || null,
    interval_days: fields.interval_days === "" || fields.interval_days == null ? null : Math.max(0, parseInt(fields.interval_days, 10) || 0),
    updated_by: user?.email ?? user?.id ?? null,
    // updated_at is set by the DB trigger.
  };
  const { error } = await supabase.from("nurture_templates").update(patch).eq("id", id);
  if (error) fail("saveTemplate", error, "Could not save the template.");
}

// ---- Steps: create / delete / reorder / copy (operate on nurture_templates) ----
export async function createStep(campaignKey, defaultTemplateId = null) {
  const { data: { user } } = await supabase.auth.getUser();
  const rows = await listTemplates(campaignKey);
  const nextStep = rows.length ? Math.max(...rows.map((r) => r.step)) + 1 : 0;
  const { data, error } = await supabase
    .from("nurture_templates")
    .insert({
      sequence_key: campaignKey, step: nextStep,
      subject: "New email", preview_text: null, html: "", text: "",
      is_active: false, template_id: defaultTemplateId, interval_days: 3,
      updated_by: user?.email ?? user?.id ?? null,
    })
    .select("id").single();
  if (error) fail("createStep", error, "Could not add a step.");
  return data?.id ?? null;
}

export async function deleteStep(id) {
  const { error } = await supabase.from("nurture_templates").delete().eq("id", id);
  if (error) fail("deleteStep", error, "Could not delete the step.");
}

// Reorder: assign step = position. Two-phase to dodge the (sequence_key, step)
// unique constraint (bump to a high temp range first, then settle to final).
export async function reorderSteps(orderedIds) {
  for (let i = 0; i < orderedIds.length; i++) {
    const { error } = await supabase.from("nurture_templates").update({ step: 1000 + i }).eq("id", orderedIds[i]);
    if (error) fail("reorderSteps.tmp", error, "Could not reorder steps.");
  }
  for (let i = 0; i < orderedIds.length; i++) {
    const { error } = await supabase.from("nurture_templates").update({ step: i }).eq("id", orderedIds[i]);
    if (error) fail("reorderSteps.final", error, "Could not reorder steps.");
  }
}

export async function copyStepToCampaign(stepId, targetCampaignKey) {
  const { data: src, error: e1 } = await supabase
    .from("nurture_templates")
    .select("subject, preview_text, html, text, template_id, interval_days")
    .eq("id", stepId).single();
  if (e1) fail("copyStepToCampaign.load", e1, "Could not load the step.");
  const rows = await listTemplates(targetCampaignKey);
  const nextStep = rows.length ? Math.max(...rows.map((r) => r.step)) + 1 : 0;
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase.from("nurture_templates").insert({
    sequence_key: targetCampaignKey, step: nextStep,
    subject: src.subject, preview_text: src.preview_text, html: src.html, text: src.text,
    is_active: false, template_id: src.template_id, interval_days: src.interval_days,
    updated_by: user?.email ?? user?.id ?? null,
  });
  if (error) fail("copyStepToCampaign.insert", error, "Could not copy the step.");
}

// ---- Campaigns (nurture_campaigns) ----
export async function listCampaigns() {
  const { data, error } = await supabase
    .from("nurture_campaigns")
    .select("id, key, name, description, default_template_id, status, created_at")
    .order("created_at", { ascending: true });
  if (error) fail("listCampaigns", error, "Could not load campaigns.");
  // attach step counts
  const out = data ?? [];
  for (const c of out) {
    c.step_count = await count("nurture_templates", (q) => q.eq("sequence_key", c.key));
  }
  return out;
}

function slugify(s) {
  return String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 50) || `campaign-${Date.now()}`;
}

export async function createCampaign({ name, defaultTemplateId = null }) {
  const key = slugify(name);
  const { data, error } = await supabase
    .from("nurture_campaigns")
    .insert({ key, name: String(name || "Untitled").slice(0, 120), default_template_id: defaultTemplateId, status: "draft" })
    .select("id, key").single();
  if (error) {
    if (error.code === "23505") throw new Error("A campaign with a similar name already exists — pick another name.");
    fail("createCampaign", error, "Could not create the campaign.");
  }
  return data;
}

export async function updateCampaign(id, fields) {
  const patch = {};
  if ("name" in fields) patch.name = String(fields.name || "").slice(0, 120);
  if ("description" in fields) patch.description = fields.description || null;
  if ("default_template_id" in fields) patch.default_template_id = fields.default_template_id || null;
  if ("status" in fields) patch.status = fields.status === "active" ? "active" : "draft";
  if (Object.keys(patch).length === 0) return;
  const { error } = await supabase.from("nurture_campaigns").update(patch).eq("id", id);
  if (error) fail("updateCampaign", error, "Could not update the campaign.");
}

export async function deleteCampaign(id, key) {
  const subs = await count("nurture_subscribers", (q) => q.eq("sequence_key", key));
  if (subs && subs > 0) throw new Error(`Can't delete — ${subs} subscriber(s) are enrolled in this campaign.`);
  await supabase.from("nurture_templates").delete().eq("sequence_key", key);
  const { error } = await supabase.from("nurture_campaigns").delete().eq("id", id);
  if (error) fail("deleteCampaign", error, "Could not delete the campaign.");
}

// ---- Email formats (nurture_email_templates — the reusable branding) ----
const FORMAT_FIELDS = "id, name, logo_url, logo_placement, header_bg_color, header_text, button_color, footer_bg_color, footer_text, is_default, updated_at";

export async function listFormats() {
  const { data, error } = await supabase
    .from("nurture_email_templates").select(FORMAT_FIELDS).order("created_at", { ascending: true });
  if (error) fail("listFormats", error, "Could not load templates.");
  return data ?? [];
}

export async function createFormat(name = "New template") {
  const { data, error } = await supabase
    .from("nurture_email_templates")
    .insert({ name: String(name).slice(0, 120) })
    .select("id").single();
  if (error) fail("createFormat", error, "Could not create the template.");
  return data?.id ?? null;
}

export async function saveFormat(id, fields) {
  const patch = {};
  for (const k of ["name", "logo_url", "logo_placement", "header_bg_color", "header_text", "button_color", "footer_bg_color", "footer_text"]) {
    if (k in fields) patch[k] = fields[k] === "" ? (k === "name" ? "Untitled" : null) : fields[k];
  }
  if ("name" in patch) patch.name = String(patch.name || "Untitled").slice(0, 120);
  const { error } = await supabase.from("nurture_email_templates").update(patch).eq("id", id);
  if (error) fail("saveFormat", error, "Could not save the template.");
}

export async function deleteFormat(id) {
  const { error } = await supabase.from("nurture_email_templates").delete().eq("id", id);
  if (error) fail("deleteFormat", error, "Could not delete the template.");
}

// Render + send one real preview via the Brevo adapter (server-side function).
export async function sendTestTemplate(sequenceKey, step, to) {
  const { data, error } = await supabase.functions.invoke("nurture-test-send", {
    body: { sequence_key: sequenceKey, step, to },
  });
  if (error) throw new Error(error.message || "Test send failed.");
  if (data && data.error) throw new Error(data.error);
  return data;
}
